import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import socket from '../utils/socket';
import { getExperiment } from '../utils/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const MQ_KEYS = ['mq2','mq3','mq4','mq5','mq6','mq7','mq8','mq9','mq135'];
const COLORS   = ['#f87171','#fb923c','#facc15','#a3e635','#34d399','#22d3ee','#818cf8','#c084fc','#f472b6'];
const ENV_KEYS = ['ens160_eco2','ens160_tvoc','aht21_temp','aht21_hum','sgp30_co2','sgp30_tvoc'];

const MAX_POINTS = 300;

function makeDataset(label, color) {
  return { label, data: [], borderColor: color, backgroundColor: color+'33',
    borderWidth:1.5, pointRadius:0, tension:0.3 };
}

function initChartData(keys, colors) {
  return {
    labels:   [],
    datasets: keys.map((k, i) => makeDataset(k.toUpperCase(), colors[i % colors.length])),
  };
}

const chartOpts = (title) => ({
  responsive: true, animation: false,
  plugins: { legend: { labels: { color:'#94a3b8', font:{size:11} } },
             title:  { display:true, text:title, color:'#cbd5e1' } },
  scales: {
    x: { ticks:{ color:'#475569', maxTicksLimit:10 }, grid:{ color:'#1e293b' } },
    y: { ticks:{ color:'#475569' }, grid:{ color:'#1e293b' } },
  },
});

export default function ExperimentProcessing() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [exp,     setExp]     = useState(null);
  const [done,    setDone]    = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mqChart  = useRef(initChartData(MQ_KEYS, COLORS));
  const envChart = useRef(initChartData(ENV_KEYS, ['#67e8f9','#a5b4fc','#fca5a5','#93c5fd','#fde68a','#d9f99d']));
  const [, forceUpdate] = useState(0);
  const timerRef = useRef(null);
  const startRef = useRef(Date.now());

  useEffect(() => {
    getExperiment(id).then(({ data }) => setExp(data)).catch(() => {});
  }, [id]);

  // Elapsed timer
  useEffect(() => {
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    const onData = (d) => {
      if (String(d.experiment_id) !== String(id)) return;
      const ts = new Date().toLocaleTimeString('th-TH');

      // MQ chart
      if (mqChart.current.labels.length >= MAX_POINTS) {
        mqChart.current.labels.shift();
        mqChart.current.datasets.forEach(ds => ds.data.shift());
      }
      mqChart.current.labels.push(ts);
      MQ_KEYS.forEach((k, i) => mqChart.current.datasets[i].data.push(d[k] ?? null));

      // ENV chart
      if (envChart.current.labels.length >= MAX_POINTS) {
        envChart.current.labels.shift();
        envChart.current.datasets.forEach(ds => ds.data.shift());
      }
      envChart.current.labels.push(ts);
      ENV_KEYS.forEach((k, i) => envChart.current.datasets[i].data.push(d[k] ?? null));

      forceUpdate(n => n + 1);
    };

    const onDone = ({ experiment_id }) => {
      if (String(experiment_id) === String(id)) {
        setDone(true);
        clearInterval(timerRef.current);
      }
    };

    socket.on('experiment:data', onData);
    socket.on('experiment:done', onDone);
    return () => { socket.off('experiment:data', onData); socket.off('experiment:done', onDone); };
  }, [id]);

  const pct = exp ? Math.min(100, Math.round(elapsed / exp.duration * 100)) : 0;

  return (
    <div style={{ maxWidth:1100, margin:'0 auto' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700 }}>{exp?.title || 'กำลังทดลอง...'}</h1>
          <p style={{ color:'#64748b', fontSize:13, marginTop:4 }}>
            Experiment ID: {id} &nbsp;|&nbsp; ระยะห่าง: {exp?.log_interval}s &nbsp;|&nbsp; ระยะเวลา: {exp?.duration}s
          </p>
        </div>
        <div style={{ textAlign:'right' }}>
          <span style={{
            background: done ? '#14532d' : '#1e3a5f',
            color: done ? '#bbf7d0' : '#7dd3fc',
            padding:'6px 18px', borderRadius:20, fontWeight:600, fontSize:14,
          }}>
            {done ? 'เสร็จสิ้น' : 'กำลังทดลอง...'}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      {!done && (
        <div style={{ background:'#1e293b', borderRadius:8, height:10, marginBottom:20, overflow:'hidden' }}>
          <div style={{ width:`${pct}%`, height:'100%', background:'#0ea5e9', transition:'width 1s linear' }} />
        </div>
      )}
      <p style={{ color:'#94a3b8', fontSize:13, marginBottom:20 }}>
        {done ? `เสร็จสิ้นใน ${elapsed}s` : `ผ่านไป ${elapsed}s / ${exp?.duration || '?'}s (${pct}%)`}
      </p>

      {/* MQ Chart */}
      <div style={{ background:'#0f172a', borderRadius:12, padding:20, marginBottom:20 }}>
        <Line data={mqChart.current} options={chartOpts('MQ Sensors (Voltage V)')} />
      </div>

      {/* ENV Chart */}
      <div style={{ background:'#0f172a', borderRadius:12, padding:20, marginBottom:20 }}>
        <Line data={envChart.current} options={chartOpts('Environmental Sensors')} />
      </div>

      {done && (
        <div style={{ display:'flex', gap:12 }}>
          <button onClick={() => navigate(`/experiments/${id}`)}
            style={{ background:'#0ea5e9', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 22px', fontWeight:600, cursor:'pointer' }}>
            ดูผลการทดลอง
          </button>
          <button onClick={() => navigate('/')}
            style={{ background:'#1e293b', color:'#94a3b8', border:'1px solid #334155',
              borderRadius:8, padding:'10px 22px', fontWeight:600, cursor:'pointer' }}>
            กลับ Lobby
          </button>
        </div>
      )}
    </div>
  );
}
